import { useEffect } from 'react';

import { styled } from '@mui/material/styles';
import { Switch, Tooltip, useColorScheme } from '@mui/material';

import { useTranslate } from 'src/locales';

import { useSettingsContext } from 'src/components/settings';

const ThemeSwitch = styled(Switch)(({ theme }) => ({
  width: 60,
  height: 34,
  padding: 7,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 4,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(26px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#e4e7ec',
        border: '1px solid #919EAB',
        opacity: 1,
        padding: '10px 0',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 20,
    height: 20,
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #919EAB',
    padding: 0.5,
    '&::before': {
      content: '""',
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundSize: 'contain',
    },
  },
  '& .MuiSwitch-thumb::before': {
    content: '""',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23000' d='M18 12a6 6 0 1 1-12 0a6 6 0 0 1 12 0'/%3E%3Cpath fill='%23000' fill-rule='evenodd' d='M12 1.25a.75.75 0 0 1 .75.75v1a.75.75 0 0 1-1.5 0V2a.75.75 0 0 1 .75-.75M4.399 4.399a.75.75 0 0 1 1.06 0l.393.392a.75.75 0 0 1-1.06 1.061l-.393-.393a.75.75 0 0 1 0-1.06m15.202 0a.75.75 0 0 1 0 1.06l-.393.393a.75.75 0 0 1-1.06-1.06l.393-.393a.75.75 0 0 1 1.06 0M1.25 12a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5H2a.75.75 0 0 1-.75-.75m19 0a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75m-2.102 6.148a.75.75 0 0 1 1.06 0l.393.393a.75.75 0 1 1-1.06 1.06l-.393-.393a.75.75 0 0 1 0-1.06m-12.296 0a.75.75 0 0 1 0 1.06l-.393.393a.75.75 0 1 1-1.06-1.06l.392-.393a.75.75 0 0 1 1.061 0M12 20.25a.75.75 0 0 1 .75.75v1a.75.75 0 0 1-1.5 0v-1a.75.75 0 0 1 .75-.75' clip-rule='evenodd'/%3E%3C/svg%3E")`,
  },

  '& .Mui-checked .MuiSwitch-thumb::before': {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cg fill='%23000' fill-opacity='0'%3E%3Cpath d='M15.22 6.03l2.53 -1.94l-3.19 -0.09l-1.06 -3l-1.06 3l-3.19 0.09l2.53 1.94l-0.91 3.06l2.63 -1.81l2.63 1.81l-0.91 -3.06Z'%3E%3Canimate fill='freeze' attributeName='fill-opacity' begin='0.7s' dur='0.4s' values='0;1'/%3E%3C/path%3E%3Cpath d='M19.61 12.25l1.64 -1.25l-2.06 -0.05l-0.69 -1.95l-0.69 1.95l-2.06 0.05l1.64 1.25l-0.59 1.98l1.7 -1.17l1.7 1.17l-0.59 -1.98Z'%3E%3Canimate fill='freeze' attributeName='fill-opacity' begin='1.1s' dur='0.4s' values='0;1'/%3E%3C/path%3E%3C/g%3E%3Cpath fill='%23000' fill-opacity='0' stroke='%23000' stroke-dasharray='56' stroke-dashoffset='56' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M7 6c0 6.08 4.92 11 11 11c0.53 0 1.05 -0.04 1.56 -0.11c-1.61 2.47 -4.39 4.11 -7.56 4.11c-4.97 0 -9 -4.03 -9 -9c0 -3.17 1.64 -5.95 4.11 -7.56c-0.07 0.51 -0.11 1.03 -0.11 1.56Z'%3E%3Canimate fill='freeze' attributeName='fill-opacity' begin='1.5s' dur='0.5s' values='0;1'/%3E%3Canimate fill='freeze' attributeName='stroke-dashoffset' dur='0.6s' values='56;0'/%3E%3C/path%3E%3C/svg%3E")`,
  },
  '& .MuiSwitch-track': {
    borderRadius: 20,
    backgroundColor: '#FFF',
    opacity: 1,
    border: '1px solid #919EAB',
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

export function ToggleThemeSwitch() {
  const { t } = useTranslate('translation');

  const settings = useSettingsContext();
  const { mode, setMode, systemMode } = useColorScheme();

  useEffect(() => {
    if (mode === 'system' && systemMode) {
      settings.setState({ colorScheme: systemMode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, systemMode]);

  const handleChange = (event: any) => {
    setMode(mode === 'light' ? 'dark' : 'light');
    settings.setState({ colorScheme: mode === 'light' ? 'dark' : 'light' });
  };

  return (
    <Tooltip title={t('TOGGLE_DARK_LIGHT_MODE')} arrow placement="bottom">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ThemeSwitch checked={settings.state.colorScheme === 'dark'} onChange={handleChange} />
      </div>
    </Tooltip>
  );
}
